import { Capacitor } from '@capacitor/core';

import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { environment } from '@env/environment';

import { TranslitPipe } from '@app/pipes/translit.pipe';
import { app } from '../app.config';
import { PushTokensService } from './push-tokens.service';
import { UtilsService } from './utils.service';

export type WebPushState = 'unsupported' | 'default' | 'granted' | 'denied';

/**
 * Firebase Cloud Messaging for the browser and the installed PWA.
 *
 * The native Android build is handled by PushNotificationsService instead, so
 * this service stays inactive inside the Capacitor WebView. The messaging SDK
 * is imported lazily to keep it out of the initial bundle.
 */
@Injectable({ providedIn: 'root' })
export class WebPushService {
  private platformId = inject(PLATFORM_ID);
  private pushTokensService = inject(PushTokensService);
  private translitPipe = new TranslitPipe();

  /** FCM registration token of this browser, null until registration succeeds. */
  public readonly token = signal<string | null>(null);
  public readonly state = signal<WebPushState>('default');

  /**
   * Reads the current permission and re-registers the token for visitors who
   * already opted in. It never prompts, so it is safe to call on startup.
   */
  async init(): Promise<void> {
    if (!this.isEligible()) {
      return;
    }

    const { isSupported } = await import('firebase/messaging');

    if (!(await isSupported())) {
      this.state.set('unsupported');
      return;
    }

    this.state.set(Notification.permission as WebPushState);

    if (Notification.permission === 'granted') {
      await this.registerToken();
    }
  }

  /** Prompts for the notification permission. Call from a user action only. */
  async enable(): Promise<void> {
    if (!this.isEligible() || this.state() === 'unsupported') {
      return;
    }

    const permission = await Notification.requestPermission();

    this.state.set(permission as WebPushState);

    if (permission !== 'granted') {
      UtilsService.showSnackbar(
        this.translitPipe.transform('Обавештења су одбијена у прегледачу.'), true
      );
      return;
    }

    const registered = await this.registerToken();

    const message = registered
      ? 'Обавештења су укључена.'
      : 'Укључивање обавештења није успело.';

    UtilsService.showSnackbar(this.translitPipe.transform(message), !registered);
  }

  private isEligible(): boolean {
    return isPlatformBrowser(this.platformId)
      && !Capacitor.isNativePlatform()
      && 'Notification' in window
      && 'serviceWorker' in navigator;
  }

  private async registerToken(): Promise<boolean> {
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

    try {
      const messaging = getMessaging(app);
      const token = await getToken(messaging, { vapidKey: environment.vapidKey });

      if (!token) {
        return false;
      }

      this.token.set(token);

      await this.pushTokensService.saveToken(token, 'web');

      onMessage(messaging, (payload) => {
        const message = payload.notification?.title ?? payload.notification?.body;

        if (message) {
          UtilsService.showSnackbar(this.translitPipe.transform(message));
        }
      });

      return true;
    } catch (error) {
      console.error('Web push registration failed', error);
      return false;
    }
  }
}
